/**
 * HTML sanitization utilities using DOMPurify.
 *
 * Provides safe HTML rendering to prevent XSS attacks.
 * Always use these utilities instead of raw dangerouslySetInnerHTML.
 */

import DOMPurify, { type Config } from "dompurify";

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Default DOMPurify configuration for user-generated content.
 * Allows basic formatting but strips dangerous elements.
 */
const DEFAULT_CONFIG: Config = {
  // Allowed tags for basic formatting
  ALLOWED_TAGS: [
    "p",
    "br",
    "b",
    "i",
    "strong",
    "em",
    "u",
    "s",
    "strike",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "code",
    "pre",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "span",
    "div",
  ],
  // Allowed attributes
  ALLOWED_ATTR: ["href", "target", "rel", "class", "id"],
  // Force links to open in new tab with security attributes
  ADD_ATTR: ["target", "rel"],
  // Forbid data: and javascript: URLs
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

/**
 * Strict configuration that only allows inline text formatting.
 * No links, no block elements.
 */
const STRICT_CONFIG: Config = {
  ALLOWED_TAGS: ["b", "i", "strong", "em", "u", "s", "strike", "br", "span"],
  ALLOWED_ATTR: ["class"],
};

/**
 * Configuration for plain text - strips all HTML.
 */
const PLAIN_TEXT_CONFIG: Config = {
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: [],
};

// =============================================================================
// SANITIZATION FUNCTIONS
// =============================================================================

/**
 * Sanitize HTML content for safe rendering.
 *
 * @param html - Raw HTML string (potentially unsafe)
 * @param config - Optional DOMPurify configuration override
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 *
 * @example
 * ```tsx
 * <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(userContent) }} />
 * ```
 */
export function sanitizeHtml(
  html: string | null | undefined,
  config?: Config
): string {
  if (!html) return "";

  // Merge with default config
  const mergedConfig = config ? { ...DEFAULT_CONFIG, ...config } : DEFAULT_CONFIG;

  // Add security attributes to links
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  const result = DOMPurify.sanitize(html, { ...mergedConfig, RETURN_TRUSTED_TYPE: false });

  // Clean up hook
  DOMPurify.removeHook("afterSanitizeAttributes");

  return result as string;
}

/**
 * Sanitize HTML with strict rules - only inline formatting.
 * Use for short user-provided text like names, titles.
 *
 * @param html - Raw HTML string
 * @returns Sanitized HTML with only basic formatting
 */
export function sanitizeHtmlStrict(html: string | null | undefined): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, { ...STRICT_CONFIG, RETURN_TRUSTED_TYPE: false }) as string;
}

/**
 * Strip all HTML tags and return plain text.
 * Use when you need text-only output.
 *
 * @param html - Raw HTML string
 * @returns Plain text with all HTML removed
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, { ...PLAIN_TEXT_CONFIG, RETURN_TRUSTED_TYPE: false }) as string;
}

/**
 * Check if a string contains potentially dangerous HTML.
 * Useful for validation before storing user input.
 *
 * @param html - String to check
 * @returns True if the string contains HTML that would be stripped
 */
export function containsUnsafeHtml(html: string | null | undefined): boolean {
  if (!html) return false;
  const sanitized = sanitizeHtml(html);
  return sanitized !== html;
}

// =============================================================================
// REACT HELPERS
// =============================================================================

/**
 * Create props for dangerouslySetInnerHTML with sanitized content.
 * Provides a safer API than manual object creation.
 *
 * @param html - Raw HTML string
 * @param strict - Use strict sanitization (default: false)
 * @returns Props object for spreading onto element
 *
 * @example
 * ```tsx
 * <div {...safeInnerHtml(userContent)} />
 * ```
 */
export function safeInnerHtml(
  html: string | null | undefined,
  strict = false
): { dangerouslySetInnerHTML: { __html: string } } {
  const sanitized = strict ? sanitizeHtmlStrict(html) : sanitizeHtml(html);
  return { dangerouslySetInnerHTML: { __html: sanitized } };
}
